import _ from 'underscore';
import { combineReducers } from 'redux';

import { preferenceNames } from '@bingads-webui-campaign-react/mca-core';

import { mcaReducerCreator, initialEntityState } from './use-mca-data/reducer-creators';
import { ccReducerCreator } from './use-cc-data/reducer-creators';
import { preferenceReducerCreator } from './use-preference/reducer-creators';

import { callReducer } from './use-call/reducer';
import { splitActionName, ActionSlices } from './common/action-utilities';

const mcaReducerKeys = [
  'SocialProfiles',
  'Posts',
  'Activities',
  'ConversationActivities',
  'Accounts',
  'ChannelUsers',
  'Campaigns',
  'Targets',
  'Ads',
  'ChannelProfiles',
  'DisapprovedCampaigns',
  'Tags',
  'Goals',
  'Keywords',
  'McaLocations',
];

const mcaReducers = (state, action) => {
  if (!action || !action.type) {
    return state;
  }

  // Initially there are no mca entity collections in the store if there is no
  // cached entities. We need to create missing collections in the store as some
  // components during initialization also trigger the action on an entity during initial
  // render.
  // Eg. read social Profile triggered by activity-type dropdown or notification banner.
  // This results in action dispatcher to be invoked and the dispatcher inturn
  // depends on entity collection's state params. If we try to dynamically
  // create the entity collection on reducer invocation, which is after the action
  // is dispatched, its too late. Hence checking and creating missing entity collections
  // with default data here.
  const missingMcaReducers = _.isEmpty(state) ?
    mcaReducerKeys :
    _.filter(mcaReducerKeys, key => !(state[key]));

  // Initialize mcaEntityCollections
  if (missingMcaReducers.length) {
    const newState = {};
    _.each(missingMcaReducers, (key) => {
      newState[key] = initialEntityState;
    });
    return newState;
  }

  const { slice, label } = splitActionName(action);
  if (slice !== ActionSlices.mcaEntity) {
    return state;
  }

  const newGlobalState = mcaReducerCreator(label)(state, action);

  return newGlobalState;
};

const ccReducers = combineReducers({
  Customers: ccReducerCreator('Customer'),
  Accounts: ccReducerCreator('Account'),
  Users: ccReducerCreator('User'),
  Countries: ccReducerCreator('Country'),
  PaymentInstruments: ccReducerCreator('PaymentInstrument'),
  SubGeographies: ccReducerCreator('SubGeography'),
  TimeZones: ccReducerCreator('TimeZone'),
  Currencies: ccReducerCreator('Currency'),
  CurrencyCountriesMappings: ccReducerCreator('CurrencyCountriesMapping'),
  Languages: ccReducerCreator('Language'),
  Notifications: ccReducerCreator('Notification'),
  TermsAndConditions: ccReducerCreator('TermsAndCondition'),
  BillingStatements: ccReducerCreator('BillingStatements'),
  EstimateTaxesAllPayments: ccReducerCreator('EstimateTaxesAllPayments'),
  BillingSummaryForAccounts: ccReducerCreator('BillingSummaryForAccounts'),
  McaPaymentContexts: ccReducerCreator('McaPaymentContexts'),
  BillNowForMcas: ccReducerCreator('BillNowForMcas'),
  DeletePIs: ccReducerCreator('DeletePIs'),
  GetCoupons: ccReducerCreator('GetCoupons'),
  GetCouponDetails: ccReducerCreator('GetCouponDetails'),
  AssociateAndRedeemCoupons: ccReducerCreator('AssociateAndRedeemCoupons'),
  AssociateCoupons: ccReducerCreator('AssociateCoupons'),
  GetCouponRedemptionsAndAssociations: ccReducerCreator('GetCouponRedemptionsAndAssociations'),
  GetCouponValidForCustomerWebsites: ccReducerCreator('GetCouponValidForCustomerWebsites'),
  SaveCouponStringForSignups: ccReducerCreator('SaveCouponStringForSignups'),
  GetCouponStringForSignups: ccReducerCreator('GetCouponStringForSignups'),
});

const preferenceReducers = combineReducers(_.chain(preferenceNames)
  .values()
  .map(preferenceName => [preferenceName, preferenceReducerCreator(preferenceName)])
  .object()
  .value());

const initialState = {
  mcaEntities: {},
  ccEntities: {},
  calls: {},
  preferences: {},
};

function rootReducer(state = initialState, action) {
  // figure out if the action is for entity operations or calls
  const { slice, label } = splitActionName(action);

  const mcaEntityState = mcaReducers(state.mcaEntities, action);

  const ccEntityState = ccReducers(state.ccEntities, action);

  const preferenceState = preferenceReducers(state.preferences, action);

  if (slice === ActionSlices.call) {
    return callReducer(state, action, label);
  }

  return {
    mcaEntities: mcaEntityState,
    ccEntities: ccEntityState,
    calls: state.calls,
    preferences: preferenceState,
  };
}

export default rootReducer;
